import React, { useEffect, useMemo, useState } from 'react';
import { useListContext } from 'framework/list';
import { DATE_FORMAT, useDateService } from 'framework/utils';
import GeneralStructureComplianceTrendChart from './charts/GeneralStructureComplianceTrendChart';
import StructureComparisonCard from './StructureComparisonCard';
import { calculateFulfillmentPercentage, getStatusColor } from './structureComparisonUtils';
import { alpha, Box, Popover, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { usePositionService, PositionType, SOFTWARE_ENGINEER_ID } from 'domain/shared/employee';
import { EMPLOYEE_COMPETENCY_LEVEL } from 'domain/shared/performanceMeasurement';
import { useTechnologyService, type Technology } from 'domain/shared/career';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { Theme } from '@mui/material/styles';
import { Rule } from './types';
import { Alert, Loading } from 'framework/ui/mui-builder';

const StructureComparisonGrid = () => {
  const { data: { items = [] } = {}, loading } = useListContext();
  const { formatDate, getMomentDateObj } = useDateService();
  const [positions, setSpecializations] = useState<PositionType[]>([]);
  const [technologies, setTechnologies] = useState<Technology[]>([]);
  const { addMessage } = useMessageService();

  const { findAll: findAllPositions } = usePositionService();
  const { findAll: findAllTechnologies } = useTechnologyService();

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [popoverData, setPopoverData] = useState<Rule | null>(null);
  const open = Boolean(anchorEl) && popoverData !== null;

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, foundData?: Rule) => {
    if (foundData) {
      setPopoverData(foundData);
      setAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverData(null);
  };

  const getStatusBackgroundColor = (theme: Theme, fulfillmentPercentage: number) => {
    let color = theme.palette.warning.main;
    if (fulfillmentPercentage === 100) color = theme.palette.success.main;
    if (fulfillmentPercentage < 0) color = theme.palette.error.light;
    return alpha(color, 0.2);
  };

  const renderContent = (foundData?: Rule) => {
    const displayPercentage = Math.abs(foundData?.fulfillmentPercentage || 0);

    return (
      <TableCell
        sx={theme => ({
          backgroundColor: foundData ? getStatusBackgroundColor(theme, foundData.fulfillmentPercentage) : 'transparent',
          border: '1px solid',
          borderColor: 'border.default',
          cursor: foundData ? 'pointer' : 'default',
        })}
        size={'small'}
        onClick={e => handlePopoverOpen(e, foundData)}
      >
        {foundData ? (
          <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
            <Typography>
              {foundData?.actual || '0'} / {foundData?.capacity || '0'}
            </Typography>
            <Typography variant="subtitle2" color={getStatusColor(foundData.fulfillmentPercentage)} pl={1}>
              {displayPercentage > 100 && '↑'}
              {displayPercentage < 100 && '↓'} ({displayPercentage}%)
            </Typography>
          </Box>
        ) : (
          '-'
        )}
      </TableCell>
    );
  };

  const averageTrendData = useMemo(() => {
    if (!items || items.length === 0) {
      return [];
    }

    const allDates = new Set<string>();
    items.forEach(rule => {
      if (rule.trend) {
        Object.keys(rule.trend).forEach(date => allDates.add(date));
      }
    });

    const sortedDates = Array.from(allDates).sort((a, b) => {
      const dateA = getMomentDateObj(a);
      const dateB = getMomentDateObj(b);
      return dateA.valueOf() - dateB.valueOf();
    });

    return sortedDates.map(date => {
      let totalPercentage = 0;
      let count = 0;

      items.forEach(rule => {
        if (rule.trend && rule.trend[date] !== undefined) {
          const actualValue = rule.trend[date];
          const percentage = calculateFulfillmentPercentage(rule.capacity, actualValue);
          totalPercentage += Math.abs(percentage);
          count += 1;
        }
      });

      const averagePercentage = count > 0 ? Math.round(totalPercentage / count) : 0;

      return {
        date,
        averagePercentage,
        formattedDate: formatDate(date, DATE_FORMAT.FULL_DATE),
        targetBottom: 0,
        targetTop: 100,
      };
    });
  }, [formatDate, getMomentDateObj, items]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [positionsData, technologiesData] = await Promise.all([findAllPositions(), findAllTechnologies()]);

        setSpecializations(positionsData as PositionType[]);
        setTechnologies(technologiesData as Technology[]);
      } catch (err) {
        addMessage(new ErrorMessage('Failed to load specializations and technologies. Please try again later.'));

        setSpecializations([]);
        setTechnologies([]);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredTotalItems = items.filter(item => item.positionId === null && item.technologyId === null);
  const totalItem = filteredTotalItems.filter(item => item.competencyLevel === null)?.[0];

  if (loading) return <Loading />;

  return (
    <>
      {(!items || items.length === 0) && (
        <Alert severity="info">There are no set qualification rules for comparison at this time.</Alert>
      )}
      {items?.length > 0 && (
        <>
          <Box sx={{ p: 2 }}>
            <Table sx={{ minWidth: '0!important', backgroundColor: 'background.paper' }}>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>Total</TableCell>
                  {Object.keys(EMPLOYEE_COMPETENCY_LEVEL).map(level => (
                    <TableCell size={'small'}>{level}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: 'bold' }}>General capacity</TableCell>
                  {renderContent(totalItem)}
                  {Object.keys(EMPLOYEE_COMPETENCY_LEVEL).map(level => {
                    const foundData = filteredTotalItems.find(item => item.competencyLevel === level);
                    return renderContent(foundData);
                  })}
                </TableRow>
                {positions.map(position => {
                  const filteredItems = items.filter(
                    item => item.positionId === position.id && item.technologyId === null,
                  );
                  if (filteredItems.length === 0) return null;

                  const filteredTotalItem = filteredItems.filter(item => item.competencyLevel === null)?.[0];

                  const rows = [];

                  rows.push(
                    <TableRow key={position.id} hover>
                      <TableCell component="th">{position.name}</TableCell>
                      {renderContent(filteredTotalItem)}
                      {Object.keys(EMPLOYEE_COMPETENCY_LEVEL).map(level => {
                        const foundData = filteredItems.find(item => item.competencyLevel === level);
                        return renderContent(foundData);
                      })}
                    </TableRow>,
                  );

                  if (position.id === SOFTWARE_ENGINEER_ID) {
                    technologies.forEach(technology => {
                      const filteredItemsByTechnology = items.filter(
                        item => item.positionId === position.id && item.technologyId === technology.id,
                      );

                      if (filteredItemsByTechnology.length === 0) return null;

                      const filteredTotalItemByTechnology = filteredItemsByTechnology.filter(
                        item => item.competencyLevel === null,
                      )?.[0];

                      rows.push(
                        <TableRow key={position.id} hover>
                          <TableCell component="th">
                            {position.name} {technology.name}
                          </TableCell>
                          {renderContent(filteredTotalItemByTechnology)}
                          {Object.keys(EMPLOYEE_COMPETENCY_LEVEL).map(level => {
                            const foundData = filteredItemsByTechnology.find(item => item.competencyLevel === level);
                            return renderContent(foundData);
                          })}
                        </TableRow>,
                      );
                    });
                  }

                  return rows;
                })}
              </TableBody>
            </Table>
            <Popover
              id="mouse-over-popover"
              open={open}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              onClose={handlePopoverClose}
            >
              {popoverData != null && <StructureComparisonCard rule={popoverData} />}
            </Popover>
          </Box>
          <GeneralStructureComplianceTrendChart averageTrendData={averageTrendData} />
        </>
      )}
    </>
  );
};
export default StructureComparisonGrid;
